import React, { useState, useRef, useContext } from 'react'
import { t } from '@lingui/macro'
import { Line } from 'rc-progress'

import {
  TopicElementModelAsEditor,
  TopicModelAsEditor,
  TopicModelAsMember
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import allowedFileExtensions from '../../../../../../shared/allowedFileExtensions'
import { getFileExtension } from '../../../../../../shared/fileNameUtils'
import { ButtonPrimary } from 'front-end/src/components'
import './DocumentElement.less'

import { EditElementWrapper } from '../EditElementWrapper'
import { useTopicForEditQuery } from '../../../../pages/topic-edit-page/TopicEditPage'
import EditorQuill from '../../../../components/common/quill/EditorQuill'
import { fileUploadInTopics } from '../../../../components/utils/file-upload/fileUploadInTopics'
import {
  debounced,
  patchElementContent,
  elementShouldStayInEditingMode
} from '../utils'
import { DocumentElement } from './DocumentElement'
import { notification } from '../../../../components/common/notifications/Notifications'
import useOnClickOutside from '../../../../app/hooks/useOnClickOutside'
import IconCloudUpload from '../../../../components/common/icon/IconCloudUpload'
import { TopicEditingContext } from '../../../topic-edit-page/TopicEditContext'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'

const EditDocumentElement = (props: {
  element: TopicElementModelAsEditor
  isMultiPage: boolean
  topic: TopicModelAsEditor | TopicModelAsMember
}) => {
  const { topic, element, isMultiPage } = props

  const { theme } = useLooopTheme()
  const wrapperRef = useRef(null)
  const { refetch } = useTopicForEditQuery()
  const { updateElement: updateElementContent } =
    useContext(TopicEditingContext)
  const content = element.data?.content || ''

  const [state, setState] = useState({
    editing: elementShouldStayInEditingMode(isMultiPage, content),
    progress: 100,
    progressText: '',
    html: content
  })

  useOnClickOutside(wrapperRef, () => {
    patchState({
      editing: elementShouldStayInEditingMode(isMultiPage, state.html)
    })
  })

  const patchState = (partial: Partial<typeof state>) => {
    setState({ ...state, ...partial })
  }

  const fileDetails = element.data?.fileDetails

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]
    const fileExtension = getFileExtension(file.name)
    const fileSize = file.size
    const maxFileSize = 1 * 1024 * 1024 * 1024 // 1GB

    if (!allowedFileExtensions.document.includes(fileExtension.toLowerCase())) {
      notification.error(t`File type "${fileExtension}" is not supported`)
      return null
    }
    if (fileSize > maxFileSize) {
      const maxValue = '1GB'
      notification.error(t`File is too big. Maximum file size is ${maxValue}.`)
      return null
    }
    const uploadProgress = (percentCompleted) => {
      patchState({
        progress: percentCompleted,
        progressText: `${t`Uploading`} ${percentCompleted}%`
      })
    }
    const uploadComplete = () => {
      patchState({ progressText: t`Scanning for viruses` })
    }
    await fileUploadInTopics({
      elementType: element.type,
      fileName: file.name,
      topicId: topic.id,
      elementId: element.id,
      blob: file,
      documentType: 'file',
      on: {
        uploadProgress,
        uploadComplete
      }
    })
    patchState({ progressText: '' })
    refetch()
  }

  const handleContentUpdate = (elementId: number, content: string): void => {
    patchState({ html: content })

    debounced(() => {
      updateElementContent(elementId, {
        data: {
          content
        }
      })
    })
  }

  const uploadFileExtensions = allowedFileExtensions.document
    .join(',.')
    .substring(1)

  let downloadDocumentFilename
  if (fileDetails && fileDetails.fileUrl) {
    const fileUrl = fileDetails.fileUrl
    downloadDocumentFilename = fileUrl.split('/').pop()
  }

  const uploadButtonText =
    fileDetails && fileDetails.fileUrl
      ? t`Select new file to upload`
      : t`Select file to upload`

  return (
    <div ref={wrapperRef}>
      <EditElementWrapper {...props}>
        <div
          onClick={() => {
            patchState({ editing: true })
          }}
        >
          {state.editing ? (
            <>
              <EditorQuill
                autofocus={true}
                value={state.html}
                onUploadProgress={(percentCompleted) => {
                  patchState({ progress: percentCompleted })
                }}
                uploadFn={async (fileName, blob) => {
                  const fileUrl = await fileUploadInTopics({
                    topicId: topic.id,
                    elementType: element.type,
                    elementId: element.id,
                    blob: blob,
                    fileName,
                    documentType: 'image',
                    on: {
                      uploadProgress: (percentCompleted) => {
                        patchState({ progress: percentCompleted })
                      }
                    }
                  })
                  refetch()
                  return fileUrl
                }}
                onChange={async (quillContent) => {
                  const content = await patchElementContent(
                    element,
                    topic,
                    quillContent,
                    (percentCompleted) => {
                      patchState({ progress: percentCompleted })
                    }
                  )

                  handleContentUpdate(element.id, content)
                }}
              />
              <div className="element-file">
                <div className="element-file-inner">
                  <div className="file-input-wrapper">
                    <input
                      type="file"
                      accept={uploadFileExtensions}
                      onChange={handleFileUpload}
                    />
                    <ButtonPrimary leftIcon={<IconCloudUpload />}>
                      {uploadButtonText}
                    </ButtonPrimary>
                  </div>
                  <div className="uploaded-file">
                    {downloadDocumentFilename}
                  </div>
                </div>
                {state.progressText ? (
                  <div className="upload-bar">
                    <div>{state.progressText}</div>
                    <Line
                      percent={state.progress}
                      strokeWidth={2}
                      trailWidth={2}
                      strokeColor={theme.colors.buttonPrimary[400]}
                    />
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <DocumentElement
              {...element}
              content={state.html}
              fileDetails={fileDetails}
              hideButton
            />
          )}
        </div>
      </EditElementWrapper>
    </div>
  )
}

export default EditDocumentElement
